import { formatOption } from './options';

export type SkillToolAction = 'Deleted' | 'Updated' | 'Used';
export type ToolCallVerb = SkillToolAction | 'Created';

export const SKILL_TOOL_ACTIONS: Record<string, SkillToolAction> = {
  delete_skill: 'Deleted',
  read_skill: 'Used',
  update_skill: 'Updated',
};

const PRESENT_TENSE_VERBS: Record<ToolCallVerb, string> = {
  Created: 'Creating',
  Deleted: 'Deleting',
  Updated: 'Updating',
  Used: 'Using',
};

const REPLY_TO_INTERNAL_CHAT_TOOL_NAME = 'reply_to_internal_chat_conversation';

export const TOOL_CALL_COUNT_SUFFIX_REGEX = /\s+\(x(\d+)\)$/;
const MIRRORED_TOOL_CALL_LINE_REGEXES = [
  /^\*Using tool - .*\*$/,
  /^\*(Creating|Reading|Updating|Deleting) skill: ".*"\*$/,
  /^\*(Creating|Deleting) memory: ".*"\*$/,
  /^\*Updating memory: ".*" -> ".*"\*$/,
  /^\*Sleeping (for|until) .+\*$/,
];

export function getSkillToolCallId(toolInput: object): string | undefined {
  const skillId = (toolInput as Record<string, unknown>).skillId;
  return typeof skillId === 'string' ? skillId : undefined;
}

export function formatToolName(toolName: string): string {
  return formatOption(
    toolName
      .replace(/originator/g, 'customer')
      .replace(/action_execution/g, 'task')
  );
}

export function toToolCallVerb(verb: ToolCallVerb, isRunning: boolean): string {
  return isRunning ? PRESENT_TENSE_VERBS[verb] : verb;
}

export type ToolCallKind = 'skill' | 'tool';

export interface ToolCallParts {
  kind: ToolCallKind;
  verb: ToolCallVerb;
  name: string;
}

export function describeToolCall({
  toolName,
  skillName,
}: {
  toolName: string;
  skillName?: string;
}): ToolCallParts {
  const skillAction = SKILL_TOOL_ACTIONS[toolName];
  return skillAction && skillName
    ? { kind: 'skill', verb: skillAction, name: skillName }
    : { kind: 'tool', verb: 'Used', name: formatToolName(toolName) };
}

export function formatToolCallLabel({
  toolName,
  skillName,
  isRunning = false,
}: {
  toolName: string;
  skillName?: string;
  isRunning?: boolean;
}): string {
  const { kind, verb, name } = describeToolCall({ toolName, skillName });
  return `${toToolCallVerb(verb, isRunning)} ${kind} - ${name}`;
}

export function isReplyToConversation(
  { toolName, toolInput }: { toolName: string; toolInput: object },
  conversationId: string
): boolean {
  return (
    toolName === REPLY_TO_INTERNAL_CHAT_TOOL_NAME &&
    (toolInput as Record<string, unknown>).conversationId === conversationId
  );
}

export function stripToolCallCountSuffix(transcript: string): string {
  return transcript.replace(TOOL_CALL_COUNT_SUFFIX_REGEX, '');
}

export function isMirroredToolCallTranscript(transcript: string): boolean {
  return transcript.split('\n').every(isMirroredToolCallLine);
}

function isMirroredToolCallLine(line: string): boolean {
  const lineWithoutCount = stripToolCallCountSuffix(line);
  return MIRRORED_TOOL_CALL_LINE_REGEXES.some((regex) =>
    regex.test(lineWithoutCount)
  );
}
